import React, { useCallback, useMemo } from 'react';
import { SelectPicker } from 'rsuite';
import { CalculatedLicense } from 'src/types/tax';

export interface FilterOption {
  name: string;
  value?: string;
}

export interface TaxFilterItem {
  options: FilterOption[];
  selectedValue?: string;
  onSelect: (value?: string) => void;
}
interface TaxFiltersProps {
  data: Array<CalculatedLicense>;
  selectedValue?: [string, string];
  onSelectedValue: (value: [string, string]) => void;
}

export const TaxFilters: React.FC<TaxFiltersProps> = ({
  data,
  selectedValue,
  onSelectedValue,
}) => {
  const periodOptions: FilterOption[] = useMemo((): FilterOption[] => {
    const allOption: FilterOption = { name: 'All Periods', value: '' };
    const periodOpts = data.map((period) => ({
      name: period.label,
      value: `${period.startDate}_${period.endDate}`,
    }));
    return [allOption, ...periodOpts];
  }, [data]);

  const licenseOptions: FilterOption[] = useMemo((): FilterOption[] => {
    const allOption = { name: 'All Licenses', value: '' };

    const licenseOpts = data
      .flatMap((period) => period.licenses)
      .reduce((prev: Array<FilterOption>, license) => {
        const value = license.licenseNumber ?? license.address;
        if (!prev.some((opt) => opt.value === value)) {
          prev.push({
            name: license.licenseNumber
              ? `${license.address} (${license.licenseNumber})`
              : license.address,
            value,
          });
        }
        return prev;
      }, []);

    return [allOption, ...licenseOpts];
  }, [data]);

  const setSelectedPeriodFilter = useCallback(
    (value?: string) => {
      onSelectedValue?.([value || '', selectedValue?.[1] || '']);
    },
    [selectedValue, onSelectedValue]
  );

  const setSelectedLicenseFilter = useCallback(
    (value?: string) => {
      onSelectedValue?.([selectedValue?.[0] || '', value || '']);
    },
    [selectedValue, onSelectedValue]
  );

  const taxFiltersData = useMemo(
    (): TaxFilterItem[] => [
      {
        options: periodOptions,
        selectedValue: selectedValue?.[0] ?? '',
        onSelect: setSelectedPeriodFilter,
      },
      {
        options: licenseOptions,
        selectedValue: selectedValue?.[1] ?? '',
        onSelect: setSelectedLicenseFilter,
      },
    ],
    [
      periodOptions,
      licenseOptions,
      selectedValue,
      setSelectedPeriodFilter,
      setSelectedLicenseFilter,
    ]
  );

  return (
    <div
      style={{
        display: 'flex',
        gap: '2px',
        flexWrap: 'wrap',
        flex: 1,
        minWidth: 0,
        maxWidth: 402,
      }}
    >
      {taxFiltersData?.map((item, index) => (
        <div
          key={index}
          style={{ flex: 1, minWidth: 130, overflow: 'hidden' }}
        >
          <SelectPicker
            labelKey="name"
            valueKey="value"
            data={item?.options}
            searchable={false}
            value={item?.selectedValue}
            onChange={(value) => item?.onSelect(value ?? undefined)}
            menuMaxHeight={240}
            cleanable={false}
            className="filter-picker"
            block
          />
        </div>
      ))}
    </div>
  );
};
